import { faUserPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startCase } from 'lodash';

import {
  ASSET_DATA_PROVIDERS,
  type Asset,
  type AssetExternalDetailDTO
} from '@/pages/Assets';

import {
  ASSET_DETAILS_PROPS,
  ASSET_PROVIDER_DETAILS_PROPS_CROWDSTRIKE,
  ASSET_PROVIDER_DETAILS_PROPS_SENTINELONE,
  ASSET_PROVIDER_DETAILS_PROPS_SIDECAR,
  ASSET_PROVIDER_DETAILS_PROPS_VMPE
} from './AssetPanel.constants';

const EXTERNAL_DETAIL_TOP_LEVEL_FIELDS = ['has_agent', 'agent_version'];

const formatAssetDetailsValues = (
  value: string | boolean | null | undefined | string[] | number[]
) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '--';
  }

  // TODO: might need to add formatting for other values like dates
  switch (value) {
    case true:
      return 'Yes';
    case false:
      return 'No';
    default:
      return value ?? '--';
  }
};

// TODO: replace anys in here once we have shape of crowdstrike and other providers data

// Helper function to get field value from external detail object or data JSON
const getFieldValue = (details: AssetExternalDetailDTO, key: string) => {
  // Special handling for fields that exist on external detail object itself
  if (EXTERNAL_DETAIL_TOP_LEVEL_FIELDS.includes(key)) {
    return (details as any)[key];
  }

  // Handle nested objects with dot notation (e.g., "network.adapters.mac_address")
  if (key.includes('.')) {
    const keys = key.split('.');

    return keys.reduce((nestedValue: any, k: string) => {
      return nestedValue?.[k];
    }, details.data);
  }

  // Default: return direct property from data
  return (details.data as any)[key];
};

export const dataProviderDetailsFields = (
  details: Asset | AssetExternalDetailDTO
) => {
  if (!details) {
    return [];
  }
  const protectedAttributes = (details as Asset).protected_attributes || [];

  if ('provider' in details) {
    let providerDetailsProps: Record<string, string>;
    switch (details?.provider) {
      case ASSET_DATA_PROVIDERS.SENTINELONE:
        providerDetailsProps = ASSET_PROVIDER_DETAILS_PROPS_SENTINELONE;
        break;
      case ASSET_DATA_PROVIDERS.CROWDSTRIKE:
        providerDetailsProps = ASSET_PROVIDER_DETAILS_PROPS_CROWDSTRIKE;
        break;
      case ASSET_DATA_PROVIDERS.VMPE:
        providerDetailsProps = ASSET_PROVIDER_DETAILS_PROPS_VMPE;
        break;
      case ASSET_DATA_PROVIDERS.SIDECAR:
        providerDetailsProps = ASSET_PROVIDER_DETAILS_PROPS_SIDECAR;
        break;
      default:
        // For unknown providers, use the data keys directly
        return Object.entries(details.data).map(([key]) => ({
          name: key,
          label: startCase(key),
          value: formatAssetDetailsValues(
            getFieldValue(details as AssetExternalDetailDTO, key)
          )
        }));
    }

    return Object.entries(providerDetailsProps).map(([key, label]) => ({
      name: key,
      label,
      value: formatAssetDetailsValues(
        getFieldValue(details as AssetExternalDetailDTO, key)
      )
    }));
  }

  return Object.entries(ASSET_DETAILS_PROPS).map(([key, label]) => ({
    name: key,
    label,
    value: formatAssetDetailsValues((details as any)[key]),
    icon: protectedAttributes.includes(key) && (
      <FontAwesomeIcon icon={faUserPen} size="xs" />
    )
  }));
};

export const getNetworkAdapters = (adapters: string[]) => {
  if (!adapters || !Array.isArray(adapters)) {
    return [];
  }

  return adapters.map((item: string) => {
    const nameMatch = item.match(/name:\s*([^,}]+)/);
    const macMatch = item.match(/mac_address:\s*([^,}]+)/);
    const ipMatch = item.match(/ip_address:\s*([^,}]+)/);

    return {
      name: nameMatch ? nameMatch[1] : 'Unknown',
      mac_address: macMatch ? macMatch[1] : 'Unknown',
      ip_address: ipMatch ? ipMatch[1] : 'Unknown'
    };
  });
};

export default dataProviderDetailsFields;
